import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

import { APP_SCROLL_SELECTOR, isRunningInNativeApp } from '@muzly/shared';

const SCROLL_KEY_PREFIX = 'muzly:scroll:';
/** Every navigation mints a fresh `location.key`, so the store needs a ceiling. */
const MAX_KEYS = 50;

const storageKey = (locationKey: string) =>
  `${SCROLL_KEY_PREFIX}${locationKey}`;

/** sessionStorage throws in private-browsing modes and when the quota is full. */
const tryStorage = <T>(read: () => T, fallback: T): T => {
  try {
    return read();
  } catch {
    return fallback;
  }
};

const recentKeys: string[] = [];

const touchKey = (key: string) => {
  const existing = recentKeys.indexOf(key);
  if (existing !== -1) {
    recentKeys.splice(existing, 1);
  }
  recentKeys.push(key);
  while (recentKeys.length > MAX_KEYS) {
    const evicted = recentKeys.shift();
    if (evicted) {
      tryStorage(
        () => sessionStorage.removeItem(storageKey(evicted)),
        undefined,
      );
    }
  }
};

const writeScroll = (key: string, top: number) => {
  tryStorage(() => {
    sessionStorage.setItem(storageKey(key), String(top));
    touchKey(key);
  }, undefined);
};

const readScroll = (key: string): number => {
  const saved = tryStorage(() => sessionStorage.getItem(storageKey(key)), null);
  const y = saved ? Number(saved) : 0;
  return Number.isFinite(y) ? y : 0;
};

const scrollRoot = (): HTMLElement | null =>
  document.querySelector<HTMLElement>(APP_SCROLL_SELECTOR);

// jsdom doesn't implement Element.scrollTo — fall back to the property so this
// is inert in tests instead of throwing.
const scrollTo = (el: HTMLElement, top: number) => {
  if (typeof el.scrollTo === 'function') {
    el.scrollTo(0, top);
  } else {
    el.scrollTop = top;
  }
};

/**
 * Native-like scroll restoration for `BrowserRouter`. Saves `.app-scroll`'s
 * `scrollTop` per `location.key` while scrolling and on unload, restores it on
 * POP (back gesture or `useGoBack`), and resets to top on PUSH/REPLACE.
 *
 * **Why the app owns this and not the transition library.** The library also
 * keeps saved positions, but it keys them by `window.history.state` and reads
 * that key immediately after awaiting the navigation. That holds for a push
 * (`pushState` is synchronous) and fails for a pop: `history.go(-1)` settles on
 * a later task, so the lookup still sees the entry being left and misses.
 * Keying by React Router's `location.key` sidesteps the race — by the time this
 * effect runs, the router has already committed the new location. Measured
 * against page-transition-core 1.3.0 on 2026-08-28.
 *
 * Restoring in a layout effect (pre-paint) matters twice over: the scroll lands
 * before the browser paints, and — under `legacyFlushSync`, which commits the
 * route inside the View Transitions callback — before the new snapshot is
 * captured, so the animation starts from the restored position with no jump.
 *
 * `BrowserRouter` cannot use React Router's `<ScrollRestoration />` (data-router
 * only); this reproduces it with `sessionStorage` under an LRU cap.
 */
export const useScrollRestoration = (): void => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const currentKeyRef = useRef(location.key);

  // The native shell drives its own transitions; letting the browser also
  // restore scroll on popstate fights the layout effect below.
  useEffect(() => {
    if (!isRunningInNativeApp() || !('scrollRestoration' in window.history)) {
      return;
    }
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    return () => {
      window.history.scrollRestoration = previous;
    };
  }, []);

  useLayoutEffect(() => {
    const el = scrollRoot();
    if (!el) {
      return;
    }
    currentKeyRef.current = location.key;
    touchKey(location.key);
    scrollTo(el, navigationType === 'POP' ? readScroll(location.key) : 0);
  }, [location.key, navigationType]);

  useEffect(() => {
    const el = scrollRoot();
    if (!el) {
      return;
    }
    let frame: number | null = null;
    let lastWritten = -1;

    const onScroll = () => {
      if (frame !== null) {
        return;
      }
      frame = window.requestAnimationFrame(() => {
        frame = null;
        const y = el.scrollTop;
        if (y === lastWritten) {
          return;
        }
        lastWritten = y;
        writeScroll(currentKeyRef.current, y);
      });
    };
    const onUnload = () => writeScroll(currentKeyRef.current, el.scrollTop);

    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('pagehide', onUnload);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('pagehide', onUnload);
      if (frame !== null) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, []);
};
